import { P } from 'components'
import LoadingV2 from 'components/LoadingV2'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { Calendar, DollarSign, RefreshCw, Users } from 'react-feather'
import Flatpickr from 'react-flatpickr'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardBody, Col, Row } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { isCoordinatorGroup, getCoordinatorMetricValue } from 'utility/Utils'
import ComplianceCard from './components/ComplianceCard'
import {
  getHoursDescriptions,
  getStandardHours,
  hasIndicationOnlyGroup,
  isIndicationOnlyGroup,
} from './components/metricDescriptions'
import CustomComplianceTable from './components/CustomComplianceTable'
import {
  refreshComplianceReport,
  useFetchFindoraDashboardListQuery,
} from './findoraDashboardListApiSlice'
import CustomButton from 'components/Buttons/CustomButton'
import { setFetchOption } from './findoraDashboardListSlice'
import BreadCrumbs from 'components/breadcrumbs'

const ListFindoraDashboard = () => {
  const dispatch = useDispatch()
  const [expandedRegions, setExpandedRegions] = useState({})
  const [expandedSuppliers, setExpandedSuppliers] = useState({})
  // const [expandedMembers, setExpandedMembers] = useState({})

  const { tag } = useSelector((state) => state.findoraDashboardList)
  // const { profile } = useSelector((s) => s.dashboard)

  const selectedMonthYear = tag?.selectedMonthYear
  const selectedGroup = tag?.group || 'All'

  const [regionOptions, setRegionOptions] = useState([
    { value: 'All', label: 'All' },
  ])

  const reportArgs = {
    month: tag?.month,
    year: tag?.year,
    group: selectedGroup,
  }
  const { data, isLoading, refetch, isFetching } =
    useFetchFindoraDashboardListQuery(reportArgs, {
      refetchOnMountOrArgChange: true,
      skip: false,
    })

  const handleRefresh = () =>
    dispatch(
      refreshComplianceReport('fetchFindoraDashboardList', reportArgs),
    )

  useEffect(() => {
    if (data?.regions && Array.isArray(data.regions)) {
      setRegionOptions((prev) => {
        const existing = new Set(prev.map((opt) => opt.value))
        const updated = [...prev]
        let added = false

        data.regions.forEach((region) => {
          if (region?.name && !existing.has(region.name)) {
            existing.add(region.name)
            updated.push({ value: region.name, label: region.name })
            added = true
          }
        })

        return added ? updated : prev
      })
    }
  }, [data?.regions])

  const isCoordinator =
    (selectedGroup && selectedGroup.toLowerCase().includes('coordinator')) ||
    isCoordinatorGroup(data) ||
    hasIndicationOnlyGroup(data)

  const complianceData = useMemo(() => {
    if (!data) return []
    const isCoord = isCoordinatorGroup(data)
    const isIndicationOnly = isCoord || isIndicationOnlyGroup(data)
    const metricDescriptions = getHoursDescriptions(
      getStandardHours(data),
      isIndicationOnly,
    )

    return [
      {
        id: 1,
        title: 'Onboarding Compliance',
        subtitle: 'Employee documentation and verification',
        tooltip: metricDescriptions.onboardingScore,
        percentage: newFormatThousandSeparator(data.onboardingScore || 0, 2),
        color: '#22c55e',
        icon: Users,
        bgColor: '#dcfce7',
        subcategories: [
          {
            label: 'Contract',
            percentage: newFormatThousandSeparator(
              data.onboarding?.contract || 0,
              2,
            ),
            tooltip: metricDescriptions.contract,
          },
          {
            label: 'Insurance',
            percentage: newFormatThousandSeparator(
              data.onboarding?.insurance || 0,
              2,
            ),
            tooltip: metricDescriptions.insurance,
          },
          {
            label: 'Min Age',
            percentage: newFormatThousandSeparator(
              data.onboarding?.minimumAge || 0,
              2,
            ),
            tooltip: metricDescriptions.minimumAge,
          },
        ],
      },
      {
        id: 2,
        title: 'Wage & Productivity',
        subtitle: 'Working conditions and compensation',
        tooltip: metricDescriptions.wageScore,
        percentage: newFormatThousandSeparator(data.wageScore || 0, 2),
        color: '#f59e0b',
        icon: DollarSign,
        bgColor: '#fef3c7',
        subcategories: [
          {
            label: isIndicationOnly ? 'Work Hours *' : 'Work Hours',
            percentage: newFormatThousandSeparator(
              data.wage?.workHours || 0,
              2,
            ),
            colorType: 'workingHours',
            tooltip: metricDescriptions.workHours,
          },
          // {
          //   label: 'No OT',
          //   percentage: newFormatThousandSeparator(
          //     data.wage?.noOvertime || 0,
          //     2,
          //   ),
          // },
          {
            label: 'Minimum Wage',
            percentage: newFormatThousandSeparator(
              data.wage?.wageAboveMinimum || 0,
              2,
            ),
            tooltip: metricDescriptions.wageAboveMinimum,
          },
          {
            label: 'Payment Logs',
            percentage: newFormatThousandSeparator(
              data.wage?.paymentLogs || 0,
              2,
            ),
            tooltip: metricDescriptions.paymentLogs,
          },
          {
            label: isIndicationOnly ? 'Productivity *' : 'Productivity',
            percentage: newFormatThousandSeparator(
              data.wage?.productivity || 0,
              2,
            ),
            tooltip: metricDescriptions.productivity,
          },
          ...(isCoord
            ? [
                {
                  label: 'Est. Hours',
                  percentage: getCoordinatorMetricValue(
                    data.wage,
                    'estimatedHours',
                  ),
                  suffix: 'h',
                  tooltip: 'Perkiraan jumlah jam kerja koordinator.',
                },
                {
                  label: 'Hourly Wage',
                  percentage: getCoordinatorMetricValue(
                    data.wage,
                    'hourlyWage',
                  ),
                  suffix: '',
                  tooltip: 'Perkiraan upah koordinator untuk setiap jam kerja.',
                },
              ]
            : []),
          // {
          //   label: 'Capacity',
          //   percentage: newFormatThousandSeparator(data.wage?.capacity || 0, 2),
          // },
        ],
      },
    ]
  }, [data])

  const toggleRegionExpansion = (regionId) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionId]: !prev[regionId],
    }))
  }

  const toggleSupplierExpansion = (supplierId) => {
    setExpandedSuppliers((prev) => ({
      ...prev,
      [supplierId]: !prev[supplierId],
    }))
  }

  // const toggleMemberExpansion = (memberId) => {
  //   setExpandedMembers((prev) => ({
  //     ...prev,
  //     [memberId]: !prev[memberId],
  //   }))
  // }

  const handleChangeGroup = (e) => {
    const val = e.target.value
    dispatch(
      setFetchOption({
        group: val,
      }),
    )
  }

  const handleChangeMonthYear = (selectedDates) => {
    if (selectedDates.length > 0) {
      const date = selectedDates[0]
      dispatch(
        setFetchOption({
          month: date.getMonth() + 1,
          year: date.getFullYear(),
          selectedMonthYear: date,
        }),
      )
    }
  }

  if (isLoading || isFetching) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'ESG'} />
      </div>
      <Row className="mb-3">
        <Col sm="12" md="4" lg="4">
          <div className="d-flex flex-column">
            <h5>ESG</h5>
            <p className="text-muted">
              Monitor worker compliance across all categories and suppliers
            </p>
          </div>
        </Col>
        <Col sm="12" md="8" lg="8">
          <div className="d-flex flex-column flex-md-row gap-3 justify-content-md-end">
            <div className="d-flex flex-column" style={{ minWidth: '240px' }}>
              <P size="medium" className="mb-2">
                Group
              </P>
              <select
                className="form-select border rounded"
                style={{ height: 42, background: '#ffffff', cursor: 'pointer' }}
                value={selectedGroup}
                onChange={handleChangeGroup}
                data-testid="findora-dashboard-group-filter"
              >
                {regionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex flex-column" style={{ minWidth: '200px' }}>
              <P size="medium" className="mb-2">
                Filter
              </P>
              <div
                className="d-flex align-items-center border rounded"
                style={{ height: 42, background: '#ffffff' }}
                data-testid="findora-dashboard-month-filter"
              >
              <Calendar size={17} className="ms-3" />
              <Flatpickr
                value={
                  selectedMonthYear
                    ? moment(selectedMonthYear).format('YYYY-MM')
                    : moment().format('YYYY-MM')
                }
                onChange={handleChangeMonthYear}
                options={{
                  altInput: true,
                  altFormat: 'F Y',
                  dateFormat: 'Y-m',
                  defaultDate: selectedMonthYear
                    ? moment(selectedMonthYear).format('YYYY-MM')
                    : moment().format('YYYY-MM'),
                  disableMobile: true,
                  maxDate: moment().format('YYYY-MM'),
                  // minDate: profile?.createdAt
                  //   ? moment(profile.createdAt).format('YYYY-MM-DD')
                  //   : undefined,
                  plugins: [
                    new MonthSelectPlugin({
                      shorthand: false,
                      dateFormat: 'Y-m',
                      altFormat: 'F Y',
                    }),
                  ],
                }}
                className="form-control bg-transparent border-0 shadow-none"
              />
            </div>
          </div>
          <div className="d-flex align-items-end">
            <CustomButton
              onClick={handleRefresh}
              outline
              color="primary"
              className="d-flex align-items-center"
              style={{ height: 42 }}
              testId="findora-dashboard-refresh-button"
              title="Perbarui data"
              aria-label="Perbarui data"
            >
              <RefreshCw size={15} />
            </CustomButton>
          </div>
        </div>
      </Col>
    </Row>

      {/* Hide compliance cards per task requirements */}
      {/* <Row>
        {complianceData.map((item, index) => (
          <Col lg="6" sm="12" key={index} className="mb-3">
            <ComplianceCard {...item} />
          </Col>
        ))}
      </Row> */}

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <CustomComplianceTable
            data={data ?? { regions: [] }}
            expandedRegions={expandedRegions}
            expandedSuppliers={expandedSuppliers}
            // expandedMembers={expandedMembers}
            // toggleMemberExpansion={toggleMemberExpansion}
            toggleRegionExpansion={toggleRegionExpansion}
            toggleSupplierExpansion={toggleSupplierExpansion}
            isCoordinator={isCoordinator}
            selectedMonthYear={selectedMonthYear}
            updatedDate={data?.to}
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default ListFindoraDashboard
